<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToTab'
});
</script>
<template>
	<div :key="langKey" class="to-tab" :class="setClass" :flex="flex ? flex : null" :style="setStyle">
		<div class="to-tab-tab">
			<div v-if="props.title || $slots.title" class="to-tab-title">
				<div class="to-tab-title-text">
					<slot name="title">{{ title }}</slot>
				</div>
			</div>
			<div ref="list" class="to-tab-list" @scroll="handleListScroll">
				<div ref="ul" class="to-tab-wrapper">
					<template v-for="(item, index) in _data" :key="index">
						<div v-if="$slots.center && Math.floor(_data.length / 2) === index">
							<slot name="center"></slot>
						</div>
						<div v-else class="to-tab-item" :data-tab-index="index" :class="{ 'is-on': item.value === selectValue, 'is-disabled': item.disabled }" :style="setTabStyle" @click="changeTab(item, index, 'click')" @mouseover="changeTab(item, index, 'hover')" @contextmenu="contextmenu(item, index)">
							<slot name="item" :item="item" :index="index">
								<div class="to-tab-item-main">
									<div class="to-tab-item-left"></div>
									<div class="to-tab-item-content">
										<to-pic v-if="item.pic" class="to-tab-item-pic" :src="item.value === selectValue ? getPicOn(item.pic) : item.pic"></to-pic>
										<to-icon v-if="item.icon" :value="item.icon" class="to-tab-item-icon" :type="item.value === selectValue ? 'fill' : ''" mode="tab-icon"></to-icon>
										<div class="to-tab-item-label">
											<slot name="label" v-bind="item">
												{{ item.label }}
											</slot>
										</div>
										<to-tag v-if="item.count" type="number" class="to-tab-item-count">{{ item.count }}</to-tag>
										<to-icon v-if="removable" value="close" link class="to-tab-item-remove" @click.stop="removePTab(item, index)"></to-icon>
									</div>
									<div class="to-tab-item-right"></div>
								</div>
								<div class="to-tab-indicator"></div>
							</slot>
						</div>
					</template>
				</div>
				<div v-if="tabScrollShow" ref="tabScrollRef" class="to-tab-list-scroll" :class="{ 'is-dragging': tabScrollDragging }" :style="tabScrollStyle" @mousedown="startTabScrollDrag">
					<div class="to-tab-list-scroll-bar"></div>
				</div>
			</div>
			<div v-if="controlShow || overflowFloatShow || slot.fn" class="to-tab-fn">
				<to-icon v-if="over === 'control' && controlShow" class="to-tab-fn-item" link value="back" @mousedown="controlLeftScroll" @mouseup="scrollStop" @mouseleave="scrollStop"></to-icon>
				<to-icon v-if="over === 'control' && controlShow" class="to-tab-fn-item" link value="next" @mousedown="controlRightScroll" @mouseup="scrollStop" @mouseleave="scrollStop"></to-icon>
				<slot name="fn"></slot>
				<to-icon v-if="overflowFloatShow" ref="overflowIconRef" value="more" link @click="toggleOverflowFloat"></to-icon>
				<Teleport v-if="overflowFloatShow" to="body">
					<div class="to-tab">
						<to-float ref="overflowFloatRef" align="right" :global="false" class="to-tab-over-float" :lazy="false" width="12">
							<to-scroll class="to-tab-over-scroll">
								<div class="to-tab-over-list">
									<div v-for="tab in overflowTabs" :key="tab.index" class="to-tab-over-item" :class="{ 'is-on': tab.item.value === selectValue, 'is-disabled': tab.item.disabled }" @click="changeOverflowTab(tab)" @contextmenu="contextmenu(tab.item, tab.index)">
										<to-pic v-if="tab.item.pic" class="to-tab-over-item-pic" :src="tab.item.value === selectValue ? getPicOn(tab.item.pic) : tab.item.pic"></to-pic>
										<to-icon v-if="tab.item.icon" :value="tab.item.icon" class="to-tab-over-item-icon" :type="tab.item.value === selectValue ? 'fill' : ''" mode="tab-icon"></to-icon>
										<div class="to-tab-over-item-label">
											<slot name="label" v-bind="tab.item">
												{{ tab.item.label }}
											</slot>
										</div>
										<to-icon v-if="removable" value="close" link class="to-tab-over-item-remove" @click.stop="removeOverflowTab(tab)"></to-icon>
									</div>
								</div>
							</to-scroll>
						</to-float>
					</div>
				</Teleport>
			</div>
			<div class="to-tab-divider"></div>
		</div>
		<to-float v-if="contentType === 'float'" ref="floatRef" :lazy="false">
			<slot></slot>
		</to-float>
		<div v-else class="to-tab-page">
			<slot></slot>
		</div>
	</div>
</template>
<script setup lang="ts">
import { ref, computed, CSSProperties, provide, Ref, onMounted, onBeforeUnmount, watch, nextTick, useSlots } from 'vue'
import { ToFloat, ToIcon } from '@touchvue/components'
import { useDevice } from '@touchvue/hooks'
import { langKey } from '@touchvue/locale'


const emit = defineEmits(['update:modelValue', 'change', 'remove', 'contextmenu'])

export interface TabItem {
	label: string
	value: string | number
	[key: string]: any
}

interface OverflowTab {
	item: TabItem
	index: number
}

export interface TabProps {
	flex?: boolean
	modelValue?: string | number
	itemWidth?: string | number
	mode?: string
	data?: TabItem[] | string
	removable?: boolean
	position?: 'left' | 'right' | 'bottom' | 'top' | ''
	itemDir?: 'v' | ''
	over?: 'float' | 'wrap' | 'control' | 'avg' | ''
	event?: string
	contentType?: string
	title?: string
	height?: string | number
	changePic?: boolean
	align?: 'center' | ''
	beforeChange?: () => boolean | Promise<boolean>
	beforeRemove?: () => boolean | Promise<boolean>
}

export interface ITabProvide {
	addTab: (item: TabItem) => void
	removeTab: (val: string | number) => void
	updateTabLabel: (val: string | number, label: string) => void
	selectValue: Ref<string, string>
}

const props = withDefaults(defineProps<TabProps>(), {
	modelValue: '',
	itemWidth: '',
	mode: 'default',
	over: '',
	itemDir: '',
	event: 'click',
	removable: false,
	data: () => [] as TabItem[],
	contentType: '',
	title: '',
	height: '',
	beforeChange: null,
	beforeRemove: null,
	changePic: false,
	position: '',
	align: '',
	flex: false
})

const selectValue: any = ref('')
const _data = ref<TabItem[]>([])
const floatRef = ref<any>(null)
const overflowFloatRef = ref<any>(null)
const overflowIconRef = ref<any>(null)
const overflowTabs = ref<OverflowTab[]>([])
const overflowFloatShow = computed(() => props.over === 'float' && overflowTabs.value.length > 0)
const ul = ref<HTMLElement | null>(null)
const list = ref<HTMLElement | null>(null)
const tabScrollRef = ref<HTMLElement | null>(null)
const tabScrollWidth = ref(0)
const tabScrollLeft = ref(0)
const tabScrollable = ref(false)
const tabScrollDragging = ref(false)
const activeOver = ref<TabProps['over']>('')
let scrollTimer: ReturnType<typeof setInterval> | null = null
const controlShow = ref(false)
let stopChange = false
let activeOverVersion = 0
const slot = useSlots()
const { isPc } = useDevice()
const tabScrollShow = computed(() => isPc.value && tabScrollable.value)
const tabScrollStyle = computed<CSSProperties>(() => {
	return {
		width: `${tabScrollWidth.value}px`,
		left: `${tabScrollLeft.value}px`
	}
})

watch(
	() => props.modelValue,
	(val, old) => {
		if (val || val === 0) {
			selectValue.value = val
			scrollSelectTabIntoView()
		} else {
			emit('update:modelValue', old)
		}
	},
	{ immediate: true }
)
watch(
	() => props.data,
	() => {
		if (!stopChange) {
			initData()
		}
	},
	{ deep: true }
)
watch(
	() => isPc.value,
	() => {
		updateTabLayout()
	}
)
watch(
	() => props.over,
	() => {
		updateTabLayout()
	}
)
onMounted(() => {
	initData()
	window.addEventListener('resize', updateTabLayout)
})
onBeforeUnmount(() => {
	window.removeEventListener('resize', updateTabLayout)
	stopTabScrollDrag()
	scrollStop()
})

function initData() {
	if (props.data && props.data.length) {
		if (typeof props.data === 'string') {
			_data.value = props.data.split(',').map(item => ({ label: item, value: item }))
		} else if (Array.isArray(props.data) && props.data.length) {
			_data.value = props.data
		}
		if (!selectValue.value && selectValue.value !== 0) {
			const hasNoOnItems = !_data.value.some(item => item.on)
			if (hasNoOnItems) {
				selectValue.value = _data.value[0].value
			}
		}
	}
	if (props.over === 'control') {
		nextTick(() => {
			setLayout()
		})
	}
	if (props.over === 'float') {
		updateOverflowTabs()
	}
	updateTabScroll()
	updateActiveOver()
}
const setTabStyle = computed(() => {
	let obj: CSSProperties = {}
	if (props.itemWidth && !isNaN(Number(props.itemWidth)) && props.itemWidth !== 'avg') {
		obj.width = props.itemWidth + 'em'
	}
	return obj
})
const setClass = computed(() => {
	let arr: string[] = []
	if (props.height === 'full') {
		arr.push(`height-full`)
	}
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	if (props.position === 'left') {
		arr.push(`position-left`)
		arr.push(`dir-v`)
	}
	if (props.position === 'right') {
		arr.push(`position-right`)
		arr.push(`dir-v`)
	}
	if (props.position === 'bottom') {
		arr.push(`position-bottom`)
	}
	if (props.itemDir) {
		arr.push(`item-dir-${props.itemDir}`)
	}
	if (props.itemWidth === 'avg') {
		arr.push(`item-width-avg`)
	}
	if (props.align) {
		arr.push(`align-${props.align}`)
	}
	if (activeOver.value) {
		arr.push(`over-${activeOver.value}`)
	}
	return arr
})

const setStyle = computed(() => {
	let obj: CSSProperties = {}
	if (props.height) {
		if (!isNaN(Number(props.height))) {
			obj.height = props.height + 'em'
		} else {
			obj.height = props.height as string
		}
	}
	return obj
})

async function changeTab(item, index, event, force = false) {
	if (!force && props.event !== event) return false
	if (item.disabled) return false
	let flag = true
	if (props.beforeChange) {
		let fn = props.beforeChange
		if (typeof fn === 'function') {
			if (typeof (fn() as Promise<boolean>).then === 'function') {
				flag = await fn()
			} else {
				flag = (fn as any)()
			}
		}
	}
	if (!flag) return false
	stopChange = true
	if (props.contentType === 'float') {
		floatRef.value.open(ul.value)
	}
	selectValue.value = item.value
	emit('update:modelValue', item.value)
	emit('change', item.value, item, index)
	scrollTabIntoView(index)
	setTimeout(() => {
		stopChange = false
	}, 10)
	return true
}

async function removePTab(item, idx) {
	let flag = true
	if (props.beforeRemove) {
		let fn = props.beforeRemove
		if (typeof fn === 'function') {
			if (typeof (fn() as Promise<boolean>).then === 'function') {
				flag = await fn()
			} else {
				flag = (fn as any)()
			}
		}
	}
	if (!flag) return false
	stopChange = true
	if (item.value === selectValue.value) {
		if (idx > 0) {
			selectValue.value = _data.value[idx - 1].value
		} else {
			selectValue.value = ''
		}
		emit('update:modelValue', selectValue.value)
	}
	_data.value.splice(idx, 1)
	if (props.over === 'control') {
		setLayout()
	}
	if (props.over === 'float') {
		updateOverflowTabs()
	}
	updateTabScroll()
	updateActiveOver()
	emit('remove', item.value, item, idx)
	setTimeout(() => {
		stopChange = false
	}, 10)
}
function removeTab(val) {
	//
	console.log(val)
}
function addTab(tab) {
	if (props.data && props.data.length) return false
	stopChange = true
	let idx = _data.value.findIndex(item => item.value === tab.value)
	if (idx === -1) {
		if (tab.on) {
			selectValue.value = tab.value
		}
		_data.value.push(tab)
		if (props.over === 'control') {
			nextTick(() => {
				setLayout()
			})
		}
		if (props.over === 'float') {
			updateOverflowTabs()
		}
		updateTabScroll()
		updateActiveOver()
	}
	setTimeout(() => {
		stopChange = false
	}, 10)
}
function updateTabLabel(oldLabel: string | number, label: string) {
	let idx = _data.value.findIndex(item => item.label === oldLabel)
	if (idx !== -1) {
		_data.value[idx].label = label
		updateTabLayout()
	}
}
function getPicOn(pic) {
	let src = ''
	if (pic && props.changePic) {
		let arr = pic.split('.')
		if (arr.length === 2) {
			src = arr[0] + '-on.' + arr[1]
		} else if (arr.length > 2) {
			arr.forEach((item, idx) => {
				if (idx === arr.length - 1) {
					src += '-on.' + item
				} else {
					src += item
				}
			})
		}
	} else {
		src = pic
	}
	return src
}
function contextmenu(item, idx) {
	emit('contextmenu', item.value, item, idx)
}
function updateTabLayout() {
	if (props.over === 'control') {
		setLayout()
	}
	updateActiveOver()
	updateOverflowTabs()
	updateTabScroll()
}
function handleListScroll() {
	setOverflowTabs()
	setTabScroll()
}
function updateOverflowTabs() {
	nextTick(() => {
		setOverflowTabs()
	})
}
function setOverflowTabs() {
	if (props.over !== 'float' || !list.value || !ul.value) {
		overflowTabs.value = []
		return
	}
	const listRect = list.value.getBoundingClientRect()
	if (!listRect.width || !listRect.height) {
		overflowTabs.value = []
		return
	}
	const hiddenIndexes = new Set<number>()
	const itemNodes = Array.from(ul.value.querySelectorAll('.to-tab-item')) as HTMLElement[]
	itemNodes.forEach(node => {
		const index = Number(node.dataset.tabIndex)
		if (Number.isNaN(index)) return
		const rect = node.getBoundingClientRect()
		const isHidden = rect.left < listRect.left - 1 || rect.right > listRect.right + 1 || rect.top < listRect.top - 1 || rect.bottom > listRect.bottom + 1
		if (isHidden) {
			hiddenIndexes.add(index)
		}
	})
	const oldShow = overflowTabs.value.length > 0
	overflowTabs.value = _data.value.map((item, index) => ({ item, index })).filter(tab => hiddenIndexes.has(tab.index))
	if (oldShow !== overflowTabs.value.length > 0) {
		updateOverflowTabs()
	}
}
function updateTabScroll() {
	nextTick(() => {
		setTabScroll()
	})
}
function setTabScroll() {
	if (!isPc.value || !list.value || !ul.value) {
		tabScrollable.value = false
		return
	}
	const listWidth = list.value.clientWidth
	const scrollWidth = ul.value.scrollWidth
	const scrollRange = scrollWidth - listWidth
	if (listWidth <= 0 || scrollRange <= 1) {
		tabScrollable.value = false
		tabScrollWidth.value = 0
		tabScrollLeft.value = 0
		list.value.scrollLeft = 0
		return
	}
	const scrollLeft = Math.max(0, Math.min(scrollRange, list.value.scrollLeft))
	if (scrollLeft !== list.value.scrollLeft) {
		list.value.scrollLeft = scrollLeft
	}
	tabScrollable.value = true
	tabScrollWidth.value = Math.max((listWidth * listWidth) / scrollWidth, 16)
	const trackRange = Math.max(listWidth - tabScrollWidth.value, 0)
	const scrollRatio = scrollLeft / scrollRange
	tabScrollLeft.value = scrollLeft + scrollRatio * trackRange
}
function getTabScrollRange() {
	if (!list.value || !ul.value) return 0
	return Math.max(ul.value.scrollWidth - list.value.clientWidth, 0)
}
async function updateActiveOver() {
	const version = ++activeOverVersion
	const over = props.over
	if (over !== 'avg' && over !== 'wrap') {
		activeOver.value = over
		return
	}
	activeOver.value = ''
	await nextTick()
	if (version !== activeOverVersion || props.over !== over || !list.value || !ul.value) return
	activeOver.value = getTabScrollRange() > 1 ? over : ''
	await nextTick()
	if (version !== activeOverVersion) return
	setTabScroll()
}
function setTabScrollLeft(scrollLeft: number) {
	if (!list.value) return
	const scrollRange = getTabScrollRange()
	list.value.scrollLeft = Math.max(0, Math.min(scrollRange, scrollLeft))
	setTabScroll()
	setOverflowTabs()
}
function startTabScrollDrag(event: MouseEvent) {
	if (!tabScrollShow.value || !list.value || !ul.value) return
	event.preventDefault()
	tabScrollDragging.value = true
	const startX = event.clientX
	const startScrollLeft = list.value.scrollLeft
	const listWidth = list.value.clientWidth
	const scrollRange = getTabScrollRange()
	const trackRange = Math.max(listWidth - tabScrollWidth.value, 1)
	const move = (moveEvent: MouseEvent) => {
		moveEvent.preventDefault()
		const nextScrollLeft = startScrollLeft + ((moveEvent.clientX - startX) / trackRange) * scrollRange
		setTabScrollLeft(nextScrollLeft)
	}
	const up = () => {
		stopTabScrollDrag()
	}
	window.addEventListener('mousemove', move)
	window.addEventListener('mouseup', up, { once: true })
	tabScrollMove = move
	tabScrollUp = up
}
let tabScrollMove: ((event: MouseEvent) => void) | null = null
let tabScrollUp: (() => void) | null = null
function stopTabScrollDrag() {
	tabScrollDragging.value = false
	if (tabScrollMove) {
		window.removeEventListener('mousemove', tabScrollMove)
		tabScrollMove = null
	}
	if (tabScrollUp) {
		window.removeEventListener('mouseup', tabScrollUp)
		tabScrollUp = null
	}
}
async function toggleOverflowFloat() {
	updateOverflowTabs()
	await nextTick()
	if (overflowTabs.value.length) {
		overflowFloatRef.value?.toggle?.(overflowIconRef.value)
	} else {
		overflowFloatRef.value?.close?.()
	}
}
async function changeOverflowTab(tab: OverflowTab) {
	const changed = await changeTab(tab.item, tab.index, 'click', true)
	if (changed !== false) {
		overflowFloatRef.value?.close?.()
		scrollTabIntoView(tab.index)
	}
}
async function removeOverflowTab(tab: OverflowTab) {
	await removePTab(tab.item, tab.index)
	updateOverflowTabs()
	await nextTick()
	if (!overflowTabs.value.length) {
		overflowFloatRef.value?.close?.()
	}
}
function scrollTabIntoView(index: number) {
	nextTick(() => {
		if (!list.value || !ul.value) return
		const node = ul.value.querySelector(`.to-tab-item[data-tab-index="${index}"]`) as HTMLElement | null
		if (!node) return
		const listRect = list.value.getBoundingClientRect()
		const nodeRect = node.getBoundingClientRect()
		if (nodeRect.right > listRect.right) {
			setTabScrollLeft(list.value.scrollLeft + nodeRect.right - listRect.right)
		} else if (nodeRect.left < listRect.left) {
			setTabScrollLeft(list.value.scrollLeft + nodeRect.left - listRect.left)
		} else {
			updateOverflowTabs()
			updateTabScroll()
		}
	})
}
function scrollSelectTabIntoView() {
	const index = _data.value.findIndex(item => item.value === selectValue.value)
	if (index !== -1) {
		scrollTabIntoView(index)
	}
}
// function calcTabFloat() {
// 	let listWidth = list.value.offsetWidth
// 	let nodeList = ul.value.querySelectorAll('.to-tab-item')
// 	let liWidths = 0
// 	let maxIdx = 0
// 	nodeList.forEach((item, idx) => {
// 		if (liWidths + item.offsetWidth < listWidth) {
// 			liWidths += item.offsetWidth
// 			maxIdx = idx
// 		}
// 	})
// }
function controlLeftScroll() {
	scrollTimer = setInterval(() => {
		setTabScrollLeft((list.value?.scrollLeft || 0) - 3)
	}, 1)
}
function controlRightScroll() {
	scrollTimer = setInterval(() => {
		setTabScrollLeft((list.value?.scrollLeft || 0) + 3)
	}, 1)
}
function scrollStop() {
	if (scrollTimer) {
		clearInterval(scrollTimer)
		scrollTimer = null
	}
}
function setLayout() {
	nextTick(() => {
		if (!list.value || !ul.value) {
			controlShow.value = false
			return
		}
		controlShow.value = getTabScrollRange() > 1
		if (!controlShow.value) {
			list.value.scrollLeft = 0
		}
		setTabScroll()
	})
}
provide<ITabProvide>('TabProvide', {
	addTab,
	removeTab,
	updateTabLabel,
	selectValue
})
</script>
